import axios from 'axios';
import { CUSTOMER_IDENTITY_API_BASE_URL } from '../config';
import { getAuthHeadersFromStorage } from '../utils/authHeaders';
import { notifyAuthFailure } from '../utils/authBus';

export const CI_PLATFORM_SESSION_STORAGE_KEY = 'ci_platform_session';

/**
 * Read the persisted platform session (set by CiAdminAuthContext on login).
 * Returns null if missing or unparseable.
 */
export const readPlatformSession = () => {
  try {
    const raw = localStorage.getItem(CI_PLATFORM_SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const writePlatformSession = (session) => {
  try {
    if (!session) {
      localStorage.removeItem(CI_PLATFORM_SESSION_STORAGE_KEY);
      return;
    }
    localStorage.setItem(CI_PLATFORM_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Ignore storage errors (private browsing, etc.).
  }
};

const client = axios.create({
  baseURL: CUSTOMER_IDENTITY_API_BASE_URL,
  withCredentials: true,
});

/**
 * The CI admin can be accessed two ways:
 *
 *  - Under `/cia/*` with a dedicated platform-user login (sends a platform
 *    JWT via `ci_platform_session`).
 *  - Under `/customer-identity/*` inside the main app (uses the main-app's
 *    OAuth/API-key for auth).
 *
 * The backend's `get_platform_principal` accepts both: a real platform JWT
 * gives a scope-restricted principal, while main-app credentials are
 * synthesized into a super-admin principal. The interceptor here routes
 * the right credentials to the right call:
 *
 *  1. Bootstrap calls (login / refresh / forgot-password / reset-password)
 *     opt in via `X-CI-Skip-Platform-Token: 1`. They need the main-app
 *     account context to resolve `account_id` before any platform JWT
 *     exists.
 *  2. When the SPA is currently inside `/cia/*`, send the platform JWT.
 *  3. Otherwise (main-app embedded mode under `/customer-identity/*`), send
 *     the main-app credentials so the backend's account-fallback path
 *     synthesizes a super-admin principal.
 */
const isOnCiaPath = () =>
  typeof window !== 'undefined' &&
  window.location?.pathname?.startsWith('/cia');

const buildAuthHeaders = ({ skipPlatformToken = false } = {}) => {
  if (skipPlatformToken) {
    return getAuthHeadersFromStorage();
  }
  if (isOnCiaPath()) {
    const session = readPlatformSession();
    if (session?.accessToken) {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.accessToken}`,
      };
    }
    // Inside /cia but no session — send no auth so the response
    // interceptor below redirects to /cia/login.
    return { 'Content-Type': 'application/json' };
  }
  // Embedded mode: defer to the main app's auth headers.
  return getAuthHeadersFromStorage();
};

client.interceptors.request.use((config) => {
  const skipPlatformToken = config.headers?.['X-CI-Skip-Platform-Token'] === '1';
  if (skipPlatformToken) {
    delete config.headers['X-CI-Skip-Platform-Token'];
  }
  const headers = buildAuthHeaders({ skipPlatformToken });
  if (Object.keys(headers).length) {
    config.headers = { ...headers, ...config.headers };
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

/**
 * When a CI admin request is rejected because the platform session is
 * missing/expired/revoked, drop the cached session and bounce the user back
 * to the login screen. We only redirect when the SPA is currently inside
 * `/cia/*`; bootstrap calls (login, forgot password) need their 401s to
 * surface to the form so the user can retry.
 */
client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isBootstrap =
      url.includes('/platform/auth/login') ||
      url.includes('/platform/auth/2fa/') ||
      url.includes('/platform/auth/refresh') ||
      url.includes('/platform/auth/password/');
    const inCiAdmin =
      typeof window !== 'undefined' &&
      window.location?.pathname?.startsWith('/cia');
    if ((status === 401 || status === 403) && !isBootstrap && inCiAdmin) {
      writePlatformSession(null);
      const here = `${window.location.pathname}${window.location.search}`;
      if (!window.location.pathname.startsWith('/cia/login')) {
        window.location.replace(
          `/cia/login?from=${encodeURIComponent(here)}`,
        );
      }
    } else if (status === 401 && !isBootstrap && !inCiAdmin) {
      notifyAuthFailure();
    }
    return Promise.reject(error);
  },
);

/**
 * Error thrown by every wrapped API call.
 *
 * Carries the stable backend error code (e.g. `user_email_already_exists`)
 * plus any structured params so the UI layer can translate it via the i18n
 * helper. Falls back to a generic `request_failed` code for transport errors.
 *
 *   try { await createCustomerUser(payload); }
 *   catch (err) {
 *     // err.code  === 'user_email_already_exists'
 *     // err.params === { constraint: 'customer_identity_users_email_unique' }
 *     // err.status === 409
 *     // err.message is the raw fallback if no translation is available.
 *   }
 */
export class CIError extends Error {
  constructor({ code, message, params, status }) {
    super(message || code || 'Request failed');
    this.name = 'CIError';
    this.code = code || 'request_failed';
    this.params = params || {};
    this.status = status;
  }
}

const formatValidationDetails = (detailArray) => {
  if (!Array.isArray(detailArray) || detailArray.length === 0) return '';
  return detailArray
    .map((d) => {
      if (!d) return '';
      if (typeof d === 'string') return d;
      const loc = Array.isArray(d.loc) ? d.loc.join('.') : '';
      const msg = d.msg || d.detail || JSON.stringify(d);
      return loc ? `${loc}: ${msg}` : msg;
    })
    .filter(Boolean)
    .join('; ');
};

const extractError = (error) => {
  // Network / transport (no response) — surface as generic network error so
  // the UI can show a friendly translated message.
  if (!error?.response) {
    return new CIError({
      code: 'network_error',
      message: error?.message || 'Network error',
      status: 0,
    });
  }
  const status = error.response.status;
  const detail = error.response.data?.detail;

  // FastAPI typically returns either a string `detail`, an object
  // `{error, ...}` (our backend's new shape), or a list of pydantic
  // validation errors.
  if (typeof detail === 'string') {
    return new CIError({ code: detail, message: detail, status });
  }
  if (Array.isArray(detail)) {
    const text = formatValidationDetails(detail);
    return new CIError({
      code: 'validation_error',
      message: text || 'Validation error',
      params: { details: text },
      status,
    });
  }
  if (detail && typeof detail === 'object') {
    const { error: code, ...rest } = detail;
    return new CIError({
      code: code || 'request_failed',
      message: code || 'Request failed',
      params: rest,
      status,
    });
  }
  return new CIError({
    code: 'request_failed',
    message: error.message || 'Request failed',
    status,
  });
};

const wrap = async (fn) => {
  try {
    const { data } = await fn();
    return data;
  } catch (error) {
    throw extractError(error);
  }
};

const PREFIX = '/api/v1/customer-identity';

/** User-group rows may arrive as snake_case or camelCase depending on client/proxy. */
function normalizeUserGroupRow(row) {
  if (!row || typeof row !== 'object') return row;
  const raw = row.group_key ?? row.groupKey;
  const group_key = raw === undefined || raw === null ? '' : String(raw).trim();
  return { ...row, group_key };
}

// ─── Sites ─────────────────────────────────────────────────────────────────

export const listSites = (includeInactive = true) =>
  wrap(() => client.get(`${PREFIX}/sites`, { params: { include_inactive: includeInactive } }));

export const getSite = (siteId) =>
  wrap(() => client.get(`${PREFIX}/sites/${siteId}`));

export const createSite = (payload) =>
  wrap(() => client.post(`${PREFIX}/sites`, payload));

export const updateSite = (siteId, payload) =>
  wrap(() => client.put(`${PREFIX}/sites/${siteId}`, payload));

export const deleteSite = (siteId) =>
  wrap(() => client.delete(`${PREFIX}/sites/${siteId}`));

// ─── Customers ─────────────────────────────────────────────────────────────

export const listCustomers = (params = {}) =>
  wrap(() => client.get(`${PREFIX}/admin/customers`, { params }));

export const getCustomer = (customerId) =>
  wrap(() => client.get(`${PREFIX}/admin/customers/${customerId}`));

export const createCustomer = (payload) =>
  wrap(() => client.post(`${PREFIX}/admin/customers`, payload));

export const updateCustomer = (customerId, payload) =>
  wrap(() => client.put(`${PREFIX}/admin/customers/${customerId}`, payload));

/**
 * Upload a customer logo (multipart). Returns the updated customer row.
 * Files are stored under the main API `/uploads/…` and `metadata.customer_logo_url` is set.
 */
export const uploadCustomerLogo = (customerId, file) =>
  wrap(() => {
    const body = new FormData();
    body.append('file', file);
    return client.post(`${PREFIX}/admin/customers/${customerId}/logo`, body);
  });

export const deleteCustomerLogo = (customerId) =>
  wrap(() => client.delete(`${PREFIX}/admin/customers/${customerId}/logo`));

export const deleteCustomer = (customerId) =>
  wrap(() => client.delete(`${PREFIX}/admin/customers/${customerId}`));

export const getCustomerHistory = (customerId, limit = 50) =>
  wrap(() => client.get(`${PREFIX}/admin/customers/${customerId}/history`, { params: { limit } }));

// ─── Customer users ────────────────────────────────────────────────────────

export const listCustomerUsers = (customerId, includeUserGroups = false) =>
  wrap(() =>
    client.get(`${PREFIX}/admin/customer-users`, {
      params: {
        customer_id: customerId,
        ...(includeUserGroups ? { include_user_groups: true } : {}),
      },
    }),
  );

/**
 * Paginated user directory. Pass `site_id` to scope one storefront; omit it to
 * list across all sites allowed for the principal (`items`, `total`, …).
 */
export const listCustomerUsersPaginated = (params = {}) =>
  wrap(() =>
    client.get(`${PREFIX}/admin/customer-users`, {
      params: { paginated: true, ...params },
    }),
  );

/**
 * @deprecated Prefer listCustomerUsersPaginated({ site_id: siteId, ... }).
 */
export const listCustomerUsersForSite = (siteId, params = {}) =>
  listCustomerUsersPaginated({ site_id: siteId, ...params });

export const setCustomerUserGroupMemberships = (userId, groupIds) =>
  wrap(() =>
    client.put(`${PREFIX}/admin/customer-users/${userId}/user-groups`, {
      group_ids: groupIds,
    }),
  );

export const createCustomerUser = (payload) =>
  wrap(() => client.post(`${PREFIX}/admin/customer-users`, payload));

export const updateCustomerUser = (userId, payload) =>
  wrap(() => client.put(`${PREFIX}/admin/customer-users/${userId}`, payload));

export const deleteCustomerUser = (userId) =>
  wrap(() => client.delete(`${PREFIX}/admin/customer-users/${userId}`));

export const getCustomerUserHistory = (userId, limit = 50) =>
  wrap(() => client.get(`${PREFIX}/admin/customer-users/${userId}/history`, { params: { limit } }));

// ─── User groups (site-scoped) ─────────────────────────────────────────────

export const listUserGroups = (siteId) =>
  wrap(async () => {
    const res = await client.get(`${PREFIX}/admin/user-groups`, {
      params: { site_id: siteId },
    });
    if (!Array.isArray(res.data)) return res;
    return {
      ...res,
      data: res.data.map(normalizeUserGroupRow),
    };
  });

export const createUserGroup = (payload) =>
  wrap(async () => {
    const res = await client.post(`${PREFIX}/admin/user-groups`, payload);
    return { ...res, data: normalizeUserGroupRow(res.data) };
  });

export const updateUserGroup = (groupId, payload) =>
  wrap(async () => {
    const res = await client.put(
      `${PREFIX}/admin/user-groups/${groupId}`,
      payload,
    );
    return { ...res, data: normalizeUserGroupRow(res.data) };
  });

export const deleteUserGroup = (groupId) =>
  wrap(() => client.delete(`${PREFIX}/admin/user-groups/${groupId}`));

export const getUserGroupMembers = (groupId) =>
  wrap(() => client.get(`${PREFIX}/admin/user-groups/${groupId}/members`));

export const setUserGroupMembers = (groupId, userIds) =>
  wrap(() =>
    client.put(`${PREFIX}/admin/user-groups/${groupId}/members`, { user_ids: userIds }),
  );

export const adminSetCustomerUserPassword = (userId, newPassword) =>
  wrap(() => client.post(`${PREFIX}/admin/customer-users/${userId}/set-password`, { new_password: newPassword }));

export const adminResetCustomerUser2fa = (userId) =>
  wrap(() => client.post(`${PREFIX}/admin/customer-users/${userId}/2fa/reset`, {}));

export const adminUpdateCustomerUser2faChannels = (userId, payload) =>
  wrap(() => client.put(`${PREFIX}/admin/customer-users/${userId}/2fa/channels`, payload));

// ─── Addresses (admin) ─────────────────────────────────────────────────────

export const listCustomerAddresses = (customerId) =>
  wrap(() => client.get(`${PREFIX}/admin/addresses`, { params: { customer_id: customerId } }));

/**
 * Paginated ship-to address directory. Pass `site_id` to scope one
 * storefront; omit it to list across all sites allowed for the principal.
 * Mirrors `listCustomerUsersPaginated` (`items`, `total`, …).
 */
export const listCustomerAddressesPaginated = (params = {}) =>
  wrap(() =>
    client.get(`${PREFIX}/admin/addresses`, {
      params: { paginated: true, ...params },
    }),
  );

export const createCustomerAddress = (payload) =>
  wrap(() => client.post(`${PREFIX}/admin/addresses`, payload));

export const updateCustomerAddress = (addressId, payload) =>
  wrap(() => client.put(`${PREFIX}/admin/addresses/${addressId}`, payload));

export const deleteCustomerAddress = (addressId) =>
  wrap(() => client.delete(`${PREFIX}/admin/addresses/${addressId}`));

export const getAddressHistory = (addressId, limit = 50) =>
  wrap(() => client.get(`${PREFIX}/admin/addresses/${addressId}/history`, { params: { limit } }));

// ─── Payment methods (admin) ───────────────────────────────────────────────

export const listCustomerPaymentMethods = (customerId, includeInactive = false) =>
  wrap(() =>
    client.get(`${PREFIX}/admin/payment-methods`, {
      params: { customer_id: customerId, include_inactive: includeInactive },
    }),
  );

export const createCustomerPaymentMethod = (payload) =>
  wrap(() => client.post(`${PREFIX}/admin/payment-methods`, payload));

export const updateCustomerPaymentMethod = (paymentMethodId, payload) =>
  wrap(() => client.put(`${PREFIX}/admin/payment-methods/${paymentMethodId}`, payload));

export const deleteCustomerPaymentMethod = (paymentMethodId) =>
  wrap(() => client.delete(`${PREFIX}/admin/payment-methods/${paymentMethodId}`));

// ─── Checkout settings (admin) ─────────────────────────────────────────────

export const getCustomerUserCheckoutSettings = (userId) =>
  wrap(() => client.get(`${PREFIX}/admin/checkout-settings/${userId}`));

export const updateCustomerUserCheckoutSettings = (userId, payload) =>
  wrap(() => client.put(`${PREFIX}/admin/checkout-settings/${userId}`, payload));

// ─── Platform users ────────────────────────────────────────────────────────

export const listPlatformUsers = () =>
  wrap(() => client.get(`${PREFIX}/admin/platform-users`));

export const getPlatformUser = (userId) =>
  wrap(() => client.get(`${PREFIX}/admin/platform-users/${userId}`));

export const createPlatformUser = (payload) =>
  wrap(() => client.post(`${PREFIX}/admin/platform-users`, payload));

export const updatePlatformUser = (userId, payload) =>
  wrap(() => client.put(`${PREFIX}/admin/platform-users/${userId}`, payload));

export const deletePlatformUser = (userId) =>
  wrap(() => client.delete(`${PREFIX}/admin/platform-users/${userId}`));

export const setPlatformUserPassword = (userId, newPassword) =>
  wrap(() =>
    client.post(`${PREFIX}/admin/platform-users/${userId}/set-password`, { new_password: newPassword }),
  );

export const getPlatformUserAccess = (userId) =>
  wrap(() => client.get(`${PREFIX}/admin/platform-users/${userId}/access`));

export const updatePlatformUserAccess = (userId, payload) =>
  wrap(() => client.put(`${PREFIX}/admin/platform-users/${userId}/access`, payload));

// ─── Platform auth (CI admin login flow) ───────────────────────────────────

/**
 * Log in a platform user. Requires an account context — the underlying
 * service uses the X-API-Key / main-app OAuth headers to resolve which
 * account the email belongs to. Returns either a 2FA challenge or token
 * payload (`tokens.access_token`, `tokens.refresh_token`, `tokens.user`).
 */
export const platformLogin = ({ email, password }) =>
  wrap(() =>
    client.post(
      `${PREFIX}/platform/auth/login`,
      { email, password },
      { headers: { 'X-CI-Skip-Platform-Token': '1' } },
    ),
  );

export const platformVerify2fa = (payload) =>
  wrap(() =>
    client.post(`${PREFIX}/platform/auth/2fa/verify`, payload, {
      headers: { 'X-CI-Skip-Platform-Token': '1' },
    }),
  );

export const platformSend2faOtp = (payload) =>
  wrap(() =>
    client.post(`${PREFIX}/platform/auth/2fa/send`, payload, {
      headers: { 'X-CI-Skip-Platform-Token': '1' },
    }),
  );

export const platformRefreshSession = (refreshToken) =>
  wrap(() =>
    client.post(
      `${PREFIX}/platform/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'X-CI-Skip-Platform-Token': '1' } },
    ),
  );

export const platformLogout = () =>
  wrap(() => client.post(`${PREFIX}/platform/auth/logout`, {}));

export const fetchPlatformMe = () =>
  wrap(() => client.get(`${PREFIX}/platform/auth/me`));

export const platformForgotPassword = ({ email }) =>
  wrap(() =>
    client.post(
      `${PREFIX}/platform/auth/password/forgot`,
      { email },
      { headers: { 'X-CI-Skip-Platform-Token': '1' } },
    ),
  );

export const platformResetPassword = ({ token, newPassword }) =>
  wrap(() =>
    client.post(
      `${PREFIX}/platform/auth/password/reset`,
      { token, new_password: newPassword },
      { headers: { 'X-CI-Skip-Platform-Token': '1' } },
    ),
  );

// ─── Setting definitions (admin) ───────────────────────────────────────────

export const listSettingDefinitions = (params = {}) =>
  wrap(() => client.get(`${PREFIX}/admin/setting-definitions`, { params }));

export const listSettingDefinitionGroups = () =>
  wrap(() => client.get(`${PREFIX}/admin/setting-definitions/groups`));

export const getSettingDefinition = (definitionId) =>
  wrap(() => client.get(`${PREFIX}/admin/setting-definitions/${definitionId}`));

export const createSettingDefinition = (payload) =>
  wrap(() => client.post(`${PREFIX}/admin/setting-definitions`, payload));

export const updateSettingDefinition = (definitionId, payload) =>
  wrap(() => client.put(`${PREFIX}/admin/setting-definitions/${definitionId}`, payload));

export const deleteSettingDefinition = (definitionId) =>
  wrap(() => client.delete(`${PREFIX}/admin/setting-definitions/${definitionId}`));

// ─── Setting values (admin) ────────────────────────────────────────────────

export const listSettingValues = (params = {}) =>
  wrap(() => client.get(`${PREFIX}/admin/setting-values`, { params }));

export const bulkUpsertSettingValues = (items) =>
  wrap(() => client.post(`${PREFIX}/admin/setting-values`, { items }));

export const clearSettingValue = ({ settingId, scopeType, scopeId }) =>
  wrap(() =>
    client.delete(`${PREFIX}/admin/setting-values`, {
      params: {
        setting_id: settingId,
        scope_type: scopeType,
        ...(scopeId ? { scope_id: scopeId } : {}),
      },
    }),
  );

export const deleteSettingValueRow = (valueId) =>
  wrap(() => client.delete(`${PREFIX}/admin/setting-values/${valueId}`));

export const getEffectiveSettingValues = (params = {}) =>
  wrap(() => client.get(`${PREFIX}/admin/setting-values/effective`, { params }));

// ─── Site API Keys ─────────────────────────────────────────────────────────

export const listSiteApiKeys = (siteId) =>
  wrap(() => client.get(`${PREFIX}/sites/${siteId}/api-keys`));

export const createSiteApiKey = (siteId, name) =>
  wrap(() => client.post(`${PREFIX}/sites/${siteId}/api-keys`, { name }));

export const revokeSiteApiKey = (siteId, keyId) =>
  wrap(() => client.delete(`${PREFIX}/sites/${siteId}/api-keys/${keyId}`));
